import { Alert, Link } from "@mui/material";
import { P, H1, Content } from "../recurso_literario.js";
import { useContext } from "react"
import { DomainContext } from "../../Context/Context.js";

export const LBIntro = () =>{
	const { cloudName } = useContext(DomainContext)

	return(
		<Content>
			<h2> Balanceador de Carga </h2>
			<P>
				El servicio de Balanceo de Carga (LBaaS en adelante) permite redirigir el tráfico
				de red desde un punto de ingreso hacia uno o varios destinos.
			</P>
			<P>
				Un LBaaS además permite armar un esquema de alta disponibilidad de servicios.
				Ya que puede ser configurado para que deje de redirigir el tráfico a un destino que
				determina con problemas para manejar dicho tráfico.
			</P>
			<P>
				Un LBaaS ofrece la capacidad de escalar un servicio de manera horizontal, sumando destinos.
			</P>
			<P>
				Para conocer los conceptos de un LBaaS dirigite a la sección Conceptos => Network => Balanceadores
			</P>
		</Content>
	)
}

export const LBActions = () =>{
	const { cloudName } = useContext(DomainContext)

	return(
		<Content>
			<H1> Listar los LBaaS </H1>
			<P>
				Podés encontrar el listado de los LBaaS que tengas desplegados en tu nube y proyecto
				dirigiéndote en el menú de la derecha a Networking => Balaceador
			</P>
			<P>
				En el listado podrás ver el nombre de los LBaaS, su estado Operativo, su condición dentro de la Nube
				así como su fecha de creación. Haciéndo click izquierdo podrás acceder a su detalle y configuración.
			</P>
			<P>
				Solo los usuarios con los siguientes permisos (asignados al usuario o al grupo al que pertenezca)
				podrán listar los LBaaS de un proyecto dado: PROJECT_ACCESS
			</P>
			<H1> Agregar LBaaS </H1>
			<P>
				Para agregar un nuevo LBaaS, tenés que utilizar el botón Agregar de la parte superior izquierda de
				la pantalla desde donde podés visualizar los LBaaS que tenés en tu proyecto. Tené en cuenta que el 
				LBaaS se desplegará en el proyecto en el que te encontrás actualmente.
			</P>
			<P>
				<img src="/img/LBaaS/LBaaS15.png" alt="Acciones de Computo" style={{width:"500px", marginLeft:"40px"}} />
			</P>
			<P>
				Verás un formulario que, a igual que el alta de VM o volumen, presenta una sección a la izquierda
				que te indicará el monto monetario detallado y a la derecha los pasos para desplegar el LBaaS.
			</P>
			<h2>Tipos de LBaaS</h2>
			<P>
				Los LBaaS, al igual que otros componentes, tienen un precio por hora. En el caso de los LBaaS, el
				precio queda definido por el tipo de LBaaS que elijas instanciar. Los hay de dos tipos
				<ul>
					<li>Básico</li>
					<li>HA</li>
				</ul>
				<P>
				Los dos ofrecen las mismas funcionalidades y características. La diferencia radica en que HA
				ofrece alta disponibilidad ante fallas en alguno de los nodos que componen la solución de {cloudName}.
				Los balanceadores <b>Básicos</b> se componen de una sola instancia. Si el nodo físico de {cloudName}
				sufre una falla, dicha instancia migra automáticamente a otro nodo disponible. Lo mismo ocurre con
				las VM. El hecho es que esta migración a otro nodo genera indisponibilidad en el servicio ya que
				se debe esperar a que levante correctamente en el nodo seleccionado.
				</P>
				<P>
				El tipo LBaaS HA levanta dos instancias del LBaaS. Cada una en un nodo diferente. Las dos no se
				encuentran ofreciendo el servicio al mismo tiempo, sino que una lo hace y la otra queda en espera.
				Si ocurriese un incidente con el nodo de {cloudName} donde se encuentra la instancia que ofrece
				el servicio, ésta se verá afectada. Pero es en ese momento cuando la instancia que estaba en espera
				en el nodo que no se vio afectado, comienza a dar servicio mientras la que sí se vio afectada levanta
				en otro nodo. Con ello se asegura un servicio continuo sin afectación hacia quienes lo consumen.
				</P>
				<Alert>
					Tanto Básico como HA ofrecen el mismo juego de configuraciones y características. La diferencia
					solo radica, como se ha indicado, en la característica de contar con una o dos instancias para
					ofrecer alta disponibilidad ante una falla en alguno de los nodos de {cloudName}.
				</Alert>
			</P>
			<h2>1er paso: Hardware</h2>
			<P>
				Debes especificar un nombre válido para el LBaaS que deseas desplegar. El tipo de balanceador
				determinará que si posee alta disponibilidad ante fallas de los nodos de {cloudName}.
			</P>
			<P>
				La red debe ser en la cual deseas ofrecer la VIP (Virtual IP) donde escucharán los servicios
				que levantes en los Puntos de Acceso. La IP de la VIP de momento no se puede especificar. Será
				elegida por el sistema al momento de instanciar el LBaaS. Posteriormente podrás asignarle una
				IP pública si necesitas otorgar servicios de LBaaS desde Internet.
			</P>
			<P>
				Se puede optar por crear el LBaaS directamente inhabilitado. Para ello se debe desactivar el
				selector "Administrativamente activo"
			</P>
			<h2>2do paso: Grupo</h2>
			<P>
				Los grupos determinan el conjunto de miembros que están preparados para recibir el tráfico que
				llega a los Puntos de Acceso. Es posible crear un LBaaS sin grupos. Es el caso donde se utilizan
				políticas de capa 7 en el Punto de Acceso y donde las mismas redirigen el tráfico a otros destinos
				HTTP.
			</P>
			<P>
				Si se opta por crear Grupos, en cada uno se debe especificar varios parámetros
			</P>
			<h3>Nombre del Grupo</h3>
			<P>
				Un nombre que sea representativo. La plataforma te indicará si el nombre es válido o no.
			</P>
			<h3>Protocolo</h3>
			<P>
				El protocolo del grupo. El cual debe ser compatible con el protocolo del Punto de Acceso
				que posteriormente definas y que envía el tráfico al Grupo. En la sección "Conceptos"
				se describe la compatibilidad entre protocolo de Grupo y de Punto de Acceso.
			</P>
			<Alert>
				Si vas a utilizar Políticas de Capa 7, tienes que elegir el protocolo HTTP
			</Alert>
			<h3>Modo de balanceo</h3>
			<P>
				Determina la forma en la que, para este grupo, el LBaaS decide a cuál de los miembros en
				el grupo se enviará el tráfico que se recibe. En la sección "Conceptos" se describe la
				diferencia entre las distintas opciones.
			</P>
			<h3>Persistencia de sesión</h3>
			<P>
				Se utiliza para dirigir al mismo miembro del grupo el tráfico de una misma sesión. Es útil
				para aplicaciones WEB donde el servidor es el que mantiene la sesión de un usuario luego
				de autenticado.
			</P>
			<P>
				Los modos de persistencia disponibles dependen del protocolo seleccionado en el Grupo. Por ejemplo,
				Si el protocolo del grupo es "UDP", solo se permite persistencia mediante "IP de origen".
			</P>
			<P>
				Dirigirse a la sección "Conceptos" para más detalle entre los diferentes métodos de Persistencia
			</P>
			<P>
				Si se elige "sin persistencia", el LBaaS utilizará el "Modo de balanceo"
				seleccionado para elegir al miembro. Si se posee alguna persistencia seleccionada, el LBaaS
				utilizara el "Modo de balanceo" para seleccionar el miembro para el primer tráfico, pero para
				los subsiguientes que sean acordes al modo de persistencia seleccionado, se enviarán al mismo
				miembro que el LBaaS seleccionó con el primer tráfico.
			</P>
			<P>
				El modo de persistencia "Cookie en APP" requiere de un campo de información adicional.
			</P>
			<h3>Persistencia de sesión</h3>
			<P>
				Al momento de crear el grupo puede optarse por inhabilitarlo. Para ello desactivar el
				selector de nombre "Administrativamente activo".
			</P>
			<h3>Miembros</h3>
			<P>
				Los miembros son las VM a donde se redirigirá el tráfico que ingresa al Grupo. Si bien
				puede desplegarse un LBaaS con un grupo sin miembros´, no tendría mucho sentido ya que
				el tráfico no se enviaría a ningún lado.
			</P>
			<P>
				De cada miembro se necesita indicar lo siguiente:
				<ul>
					<li><b>Nombre: </b>Un nombre válido que permita identificarlo</li>
					<li><b>IP: </b>La IP del miembro. Podría no estar en la misma red que la IP
					del LBaaS. Pero debe haber una ruta para poder alcanzarla</li>
					<li><b>Puerto: </b>El puerto del servicio que escucha en el miembro. Puede
					no ser el mismo puerto del Punto de Acceso. Eso permite realizar PAT(Port
					Address Translation), una técnica muy utilizada en Networking.</li>
					<li><b>Peso: </b>Es la preferencia por que el tráfico sea enviado a un miembro
					en relación a otro cuando el LBaaS debe determinar el miembro mediante el
					"Modo de balanceo" seleccionado en el Grupo. Si todos los miembros poseen
					el mismo peso, no habrá preferencia entre un miembro y otro a causa del peso.
					Para más información dirigirse a "Conceptos" </li>
					<li><b>Backup: </b>Determina si el miembro se comportará como backup o no.
					Dirigirse a "Conceptos" para más información</li>
					<li><b>Administrativamente activo: </b> Puede indicarse que un miembro se encuente
					inicilmente inactivo desde la creación del LBaaS.</li>
				</ul>
			</P>
			<h3>Monitor de estado</h3>
			<P>
				El monitor de estado le permite monitorear al LBaaS el estado de los miembros para determinar
				si están aptos para recibir tráfico. Referirse a "Conceptos" para aprender más sobre el Monitor
				de estado.
			</P>	
			<P>
				La configuración del Monitor de estado requiere de lo siguiente:
				<ul>
					<li><b>Tipo: </b> Determina la forma en la que se determina si un miembro está o no
					en condiciones de recibir el tráfico. No todos los tipos de tráfico deben ser monitoreados
					del mismo modo. Dependiendo del Tipo, será si se requiren configurar opciones adicionales.
					Es el caso si se selecciona "HTTP" o "HTTPS"</li>
					<li><b>Retraso (Delay):</b> Es el tiempo entre chequeo y chequeo realizado por el Monitor de
					estado. El valor del este campo debe ser mayor al valor el campo "Tiempo máximo (Timeout)"</li>
					<li><b>Máximos intentos:</b> Es la cantidad de chequeos consecutivos que un recurso
					considerado no apto debe pasar para que sea considerado nuevamente apto para recibir tráfico</li>
					<li><b>Máximos intentos para caída:</b> Es la cantidad de chequeos fallidos consecutivos que
					un recurso considerado apto debe pasar para que sea considerado no apto.</li>
					<li><b>Tiempo máximo (Timeout):</b> Es el tiempo que puede tardar un miembro en responder al chequeo
                    por parte del Monitor de estado. Si tarda más del valor en segundos indicado en este campo,
                    se lo considera que no ha pasado el chequeo.</li>
					<li><b>Códigos HTTP esperados: </b>Se necesita de este campo si el tipo es "HTTP" o "HTTPS".
					Determina el o los códigos que se esperan recibir al realizar una consulta a la URL
					indicada en "URL HTTP" mediante "Método HTTP". Si se recibe alguno de los códigos se
					considera que el miembro ha pasado el chequeo. Sino se considera que no está apto para
					recibir el tráfico</li>
					<li><b>Método HTTP:</b>Se necesita de este campo si el tipo es "HTTP" o "HTTPS". Es el verbo
					o método HTTP a utilizar para el chequeo.</li>
					<li><b>URL HTTP:</b>Se necesita de este campo si el tipo es "HTTP" o "HTTPS". Es la parte 
					de una URL que determina el recurso. Por ejemplo: para "www.misitio.com.ar/index.html", la
					parte que determina el recurso es "/index.html". Para "www.misitio.com.ar", la parte del
					recurso está implícita y debemos cargar, en este campo, simplemente "/".</li>
				</ul>
			</P>	

			<h2>2do paso: Punto de Acceso</h2>
			<P>
				Los puntos de acceso determinan el tipo de tráfico que ingresará por la VIP del LBaaS y a donde
				será redirigido.
			</P>
			<P>
				Se puede crear un LBaaS sin definir Puntos de Acceso. Pero no tendría mucho sentido.
			</P>
			<P>
				La definición de un Punto de Acceso requiere los siguientes campos:
				<ul>
					<li><b>Nombre del Acceso:</b> Un nombre representativo para identificar al Punto de Acceso</li>
					<li><b>Protocolo:</b> El protocolo donde escuchará el punto de acceso. Si el protocolo es
					HTTP, se nos habilitará la opción para crear Políticas de capa 7.</li>
					<li><b>Puerto:</b> El puerto del protocolo seleccionado en el paso anterior. No puede haber
					dos Puntos de acceso donde Protocolo y Puerto coincidan. Tampoco puede haber dos Puntos de
					acceso donde ambos sean TCP,HTTP o HTTPS y coincidan sus puertos. </li>
					<li><b>Grupo: </b> Es el grupo definido en el paso anterior a donde redirigir el tráfico una
					vez que ingresa por el Punto de Acceso. Puede no seleccionarse ningún grupo, en cuyo caso
					el tráfico será descartado. Si el Protocolo del Punto de Acceso es HTTP, puede no dirigirse
					el tráfico hacia ningún Grupo y usar las Políticas de capa 7.</li>
				</ul>
			</P>
			<h3>Políticas de capa 7</h3>
			<P>
				Las políticas de capa 7 se permitirán cargar desde la interfaz cuando el protocolo del Punto de
				acceso sea HTTP.
			</P>
			<P>
				Ir a la sección "Conceptos" para conocer más sobre las políticas de capa 7
			</P>
			<h2>2do paso: Resumen</h2>
			<P>
				El último paso te permite verificar que toda la configuración del nuevo LBaaS esté acorde a tus
				necesidades. Siempre puedes regresar en el formulario para cambiar algo y podrás también realizar
				cambios luego de creado el LBaaS.
			</P>
			<Alert severity="info">
				Si bien se puede agregar, borrar y modificar Grupos y Puntos de Acceso posteriormente de la creación
				del LBaaS, ten en cuenta qué una vez creado un Grupo, no podrás modificar su protocolo, lo
				que podría demandar borrar el Grupo. Para un Punto de Acceso, las características que no se pueden
				modificar son protocolo y puerto.
			</Alert>

			<H1> Gestión de un LBaaS </H1>
			<P>
				La pantalla principal, que se puede apreciar en la siguiente imagen, te mostrará información
				general del LBaaS.
			</P>
			<P>
				<img src="/img/LBaaS/LBaaS16.png" alt="Show LBaaS" style={{maxWidth:"90%", marginLeft:"40px"}}/>
			</P>
			<P>
				En Detalle podrás ver el estado general del LBaaS.
				<ul>
					<li><b>Nombre: </b> El nombre con el que identificas al LBaaS. Puedes editarlo mediante
					el botón del "lápiz" que se encuentra a la derecha del mismo.</li>
					<li><b>Condición: </b> Determina cómo se encuentra el servicio. Deberías verlo siempre en
					"Desplegada". Si se está realizando algún cambio en el mismo verás una animación de
					puntos indicando que se está realizando algún cambio en el LBaaS por ti u otro usuario.</li>
					<li><b>Estado Operativo</b>: Determina el estado del LBaaS
					<ul>
						<li><b>OnLine:</b> El LBaaS se encuentra habilitado y ofreciendo servicio. Para deshabilitarlo
						se debe hacer mediante el botón de acciones de la barra inferior de nombre "deshabilitar".
						Qué el LBaaS se encuentre OnLine no significa que sus Puntos de Acceso lo estén.</li>
						<li><b>OffLine:</b> El LBaaS se encuentra deshabilitado y no presta servicio. Para habilitarlo
						nuevamente se debe hacerlo mediante el botón de acciones de la barra inferior de nombre
						"Habilitar"</li>
					</ul></li>
					<li><b>Tipo: El tipo de LBaaS desplegado</b></li>
					<li><b>Creado:</b> La fecha en la que fue creado el servicio</li>
					<li><b>Modificado:</b> La fecha más reciente en la que ha sufrido algún cambio por parte
					de algún usuario del sistema. Puede ser un cambio en su configuración como en su estado operativo</li>
				</ul>
			</P>
			<P>
				En la sección superior derecha podrás ver el consumo del LBaaS. Solo se visualizará si el usuario
				que utilizas tiene los permisos suficientes.
			</P>
			<P>
				En la sección "Puerto" tendrás información sobre su puerto y red a la que se encuentra conectado.
				<ul>
				<li><b>Ip privada</b>: La IP de la red donde se encuentra desplegado. No es posible de momento
				cambiarla. Todo LBaaS se conecta a alguna red privada.</li>
				<li><b>Red externa:</b> Nos indica si la red a la cual se encuentra conectado, tiene salida a
				Internet. Si la tiene, entonces potencialmente podemos adjuntarle una IP pública para ofrecer
				acceso desde Internet al LBaaS. Si se encuentra con acceso a la red Externa, se podrá visualiazar
				en la barra de tareas la acción "Adjuntar IP pública"</li>
				<li><b>IP pública:</b> Nos indica la IP pública que tenemos adjuntada a la IP privada. Podríamos
				no tener IP pública adjuntada.</li>
				</ul>
			</P>
			<h2> Cambiar nombre </h2>
			<P>
				Para cambiar el nombre el LBaaS dirigite al ícono <span><img src='/img/pen.png'/>
				</span> a la altura del nombre.
			</P>
			<h2> Habilitar y inhabilitar </h2>
			<P>
				<img src="/img/Computo/VM/acc_prender.png" alt="Abilitar LBaaS"/>
				<img src="/img/Computo/VM/acc_apagar.png" alt="Inhabilitar LBaaS"/>
			</P>
			<P>
				Permite habilitar o inhabilitar el LBaaS. Como consecuencia de la inhabilitación, el LBaaS
				dejará de aceptar tráfico y dirigirlo hacia los miembros.
			</P>
			<h2> Mover de Proyecto </h2>
			<P>
				<img src="/img/Computo/VM/acc_mover.png" alt="Mover LBaaS de proyecto"/>
			</P>
			<P>
				Permite mover el LBaaS desde el proyecto donde está a otro proyecto. Al mover
				un LBaaS a otro proyecto, también se moverán de proyecto la IP Pública que
				pudiese tener.

				Al presionar el botón, se te presentará una ventana donde deberás indicar el
				proyecto destino y luego confirmar la acción.
			</P>
			<P>
				<img src="/img/Computo/VM/acc_mover2.png" alt="Popup mover" style={{width:"400px", marginLeft:"40px"}}/>
			</P>

			<h2> Adjuntar y desadjuntar IP pública </h2>
			<P>
                <img src="/img/Computo/VM/acc_asignarIP.png" alt="Adjuntar y desadjuntar IP pública"/>
            </P>
			<P>
				Los LBaaS pueden adquirir una IP pública para recibir tráfico directo desde Internet. Para adjuntarle
				una IP pública, presioná el botón. Se presentará una ventana emergente como la siguiente donde
				debés elegir una IP pública que tengas previamente adquirida para la Nube.
            </P>
			
			<h2> Eliminar un LBaaS </h2>
			<P>
                <img src="/img/Computo/VM/acc_eliminar.png" alt="Eliminar VM"/>
            </P>
			<P>
				Permite eliminar completamente un LBaaS y todas sus configuraciones. Si el LBaaS tenía adjuntada
				una IP pública, la misma será liberada pero no eliminada y podrás utilizarla en otro LBaaS o una VM.
			</P>
		</Content>
	)
}

export const LBListenerActions = () =>{
	const { cloudName } = useContext(DomainContext)

	return(
		<Content>
			<H1> Acciones sobre los Puntos de Accesos </H1>
			<P>
				Si estándo dentro de un LBaaS te diriges a la solapa "Puntos de acceso", podrás ver
				todos los Puntos de acceso (también llamados Listeners en Inglés) que posee el LBaas.
			</P>
			<P>
                <img src="/img/LBaaS/LBaaS24.png" alt="LBaaS Punto" style={{width:"100%"}}/>
            </P>
			<P>
				A la izquierda de la pantalla tendrás un listado de los mismos. Y a la derecha el detalle del
				Punto de Acceso seleccionado del listado.
			</P>
			<P>
				En a sección "Detalle" tendrás información sobre el Punto de Acceso seleccionado:
				<ul>
					<li><b>Nombre: </b>El nombre por el cual se lo conoce al Punto de Acceso. Puedes cambiarlo
					usando el "lapiz" a la derecha del nombre o yendo a "Configurar" en la barra inferior de
					acciones</li>
					<li><b>Protocolo: </b>El protocolo en el cual "escucha" el Punto de Acceso en la VIP</li>
					<li><b>Puerto: </b>El puerto del protocolo en el cual "escucha" el Punto de Acceso en la VIP</li>
					<li><b>Creado: </b>La fecha en la cual fue creado el Punto de Acceso</li>
					<li><b>Habilitado: </b>Determina si administrativamente se encuentra habilitado
					el punto de Acceso</li>
					<li><b>Modificado: </b>Fecha de la última modificación de Punto de Acceso</li>
					<li><b>Estado Operativo: </b>El estado del Punto de Acceso. Ir a "Conceptos" para más detalles
					de los posibles estados por los que puede pasar un Punto de Acceso</li>
					<li><b>Grupo: </b>El grupo al cual el Punto de Acceso redirige el tráfico. Si el Punto de Acceso
					utiliza Políticas de Capa 7, puede no redirigir tráfico a Grupo alguno</li>
				</ul>
			</P>
			<h2> Agregar punto de Acceso</h2>
			<P>
                <img src="/img/LBaaS/LBaaS17.png" alt="Agregar Punto de Acceso"/>
            </P>
			<P>
				Los Puntos de Acceso se pueden agregar a un LBaaS ya existente. Los campos son los mismos
				detallados en el alta de un LBaaS.
			</P>
			<P>
				Una vez creado el Punto de Acceso, solo es posible cambiar el Nombre y Grupo posteriormente. No
				vas a poder cambiar el Protocolo o puerto. Las Políticas de capa 7 podrás cambiarlas solamente
				si el puerto es "HTTP" al momento de crear el Punto de Acceso.
			</P>
			<h2> Configurar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS18.png" alt="Configurar Punto Acceso"/>
            </P>
			<P>
				Permite cambiar el Nombre y Grupo del Punto de Acceso.
			</P>

			<h2> Habilitar y Deshabilitar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS21.png" alt="Habilitar y deshabilitar"/>
            </P>
			<P>
				Permite cambiar el estado del Punto de Acceso de "online" a "offline" y viceversa.
			</P>
			<h2> Modificar Políticas L7 </h2>
			<P>
                <img src="/img/LBaaS/LBaaS19.png" alt="Configurar Layer 7"/>
            </P>
			<P>
				Esta acción solo es visible si el Punto de Acceso fue creado con el protocolo HTTP.
				Te permitirá cambiar las políticas de capa 7 definidas para el Punto de Acceso.
			</P>

			<h2> Eliminar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS20.png" alt="Eliminar Punto Acceso"/>
            </P>
			<P>
				Te permitirá eliminar un Punto de Acceso previa confirmación mediante una pantalla emergente.
			</P>
		</Content>
	)
}

export const LBPoolActions = () =>{
	const { cloudName } = useContext(DomainContext)

	return(
		<Content>
			<H1> Acciones sobre los Grupos </H1>
			<P>
				Si estándo dentro de un LBaaS, te diriges a la solapa "Grupos", podrás ver
				todos los Grupos (también llamados Pools en Inglés) que posee el LBaas.
			</P>
			<P>
                <img src="/img/LBaaS/LBaaS24.png" alt="LBaaS Punto" style={{width:"100%"}}/>
            </P>
			<P>
				A la izquierda de la pantalla tendrás un listado de los mismos. Y a la derecha el detalle del
				Grupo seleccionado del listado.
            </P>
			<P>
				A la derecha tendrás los detalles del Grupo. Entre ellos están:
				<ul>
					<li><b>Nombre: </b>Un nombre de fantasía que te permita identificar al Grupo</li>
					<li><b>Protocolo: </b>El protocolo del Grupo y por ende, de los miembros del mismo</li>
					<li><b>Algoritmo: </b>El mecanismo por el cual el Grupo selecciona el miembro al cual
					enviar el tráfico. Ir a la sección "Conceptos" para más detalles.</li>
					<li><b>Persistencia: </b>El mecanismo seleccionado para mantener la persistencia de las
					sesiones. Ir a la sección "Conceptos" para más detalles</li>
					<li><b>Habilitado: </b>Indica si el Grupo está o no habilitado de forma deliverada por
					el usuario</li>
					<li><b>Estado Operativo</b>: El estado en el que se encuentra el Grupo. Dependerá en
					gran medida de si se encuetra Habilitado y del estado de sus miembros. Ir a la
					sección "Conceptos" para más detalles</li>
				</ul>
            </P>
			<P>
				A la derecha también podrás observar la sección de Miembros que conforman el Grupo
            </P>

			<h2> Agregar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS17.png" alt="Agregar Punto de Acceso"/>
            </P>
			<P>
				Permite agregar un nuevo Grupo. Una vez creado el Grupo, no podrás modificar su
				protocolo.
            </P>
			<P>
				La creación del Grupo presenta los mismo campos para crearlos en el alta de un LBaaS.
            </P>

			<h2> Habilitar y Deshabilitar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS21.png" alt="Habilitar y deshabilitar"/>
            </P>
			<P>
				Permite cambiar el estado del Grupo de "online" a "offline" y viceversa.
			</P>

			<h2> Configurar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS18.png" alt="Configurar Punto Acceso"/>
            </P>
			<P>
				Permite configurar algunos atributos del Grupo como ser:
				Nombre, Modo de balanceo, Persistencia, Estado Administrativo.
			</P>

			<h2> Miembros </h2>
			<P>
                <img src="/img/LBaaS/LBaaS22.png" alt="Configurar Punto Acceso"/>
            </P>
			<P>
				Permite modificar los miembros del grupo.
			</P>

			<h2> Monitor de Estado </h2>
			<P>
                <img src="/img/LBaaS/LBaaS23.png" alt="Configurar Punto Acceso"/>
            </P>
			<P>
				Permite modificar el monitoreo del grupo.
			</P>
			<h2> Eliminar </h2>
			<P>
                <img src="/img/LBaaS/LBaaS20.png" alt="Eliminar Punto Acceso"/>
            </P>
			<P>
				Te permitirá eliminar un Grupo previa confirmación mediante una pantalla emergente.
			</P>
		</Content>
	)
}
